import React from "react";
import { Helmet } from "react-helmet-async";

export default function BrasserieCarougeoise() {
	const titlePage =
		"Brasserie Carougeoise | Site restaurant gastronomique avec réservation en ligne | Weblax Studio";
	const description =
		"Weblax a conçu le site web de la Brasserie Carougeoise : design gastronomique haut de gamme, système de réservation en ligne intégré, menu digital et SEO local. +120% de réservations en ligne dès le premier mois.";
	const url = "https://weblax.fr/realisations/brasserie-carougeoise";

	const features = [
		{
			icon: "📅",
			title: "Réservation en ligne intégrée",
			desc: "Système de réservation temps réel avec gestion des créneaux, capacité par service et confirmation automatique par email et SMS — sans dépendance à un outil tiers coûteux.",
		},
		{
			icon: "🍽️",
			title: "Menu digital dynamique",
			desc: "Cartes des menus structurées et mises à jour facilement par le restaurant — avec photos plats, allergènes, suggestions du chef et formules saisonnières.",
		},
		{
			icon: "📍",
			title: "SEO local restaurant",
			desc: "Stratégie de référencement ciblée sur les requêtes restaurant Carouge, brasserie Genève, réservation table Carouge — avec balisage schema.org Restaurant et LocalBusiness.",
		},
		{
			icon: "📱",
			title: "Expérience mobile premium",
			desc: "Site pensé pour les utilisateurs en déplacement qui cherchent une table sur mobile : réservation en 3 clics, accès rapide aux menus et localisation directe.",
		},
		{
			icon: "🎨",
			title: "Direction artistique gastronomique",
			desc: "Identité visuelle sobre et élégante, inspirée des codes de la gastronomie française : typographies serif, palette chaude et photos culinaires mises en scène.",
		},
		{
			icon: "⭐",
			title: "Avis & réputation",
			desc: "Intégration des avis Google et TripAdvisor directement sur le site, renforçant la preuve sociale et la confiance des nouveaux visiteurs.",
		},
	];

	const results = [
		{
			icon: "📅",
			metric: "+120%",
			label: "Réservations en ligne",
			color: "text-amber-700",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
		{
			icon: "📱",
			metric: "-50%",
			label: "Appels pour réserver",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "🔍",
			metric: "Top 3",
			label: "SEO restaurant Carouge",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
		{
			icon: "⏱️",
			metric: "+85%",
			label: "Temps sur le site",
			color: "text-rose-600",
			bg: "from-rose-50 to-pink-50",
			border: "border-rose-100",
		},
	];

	const timeline = [
		{
			step: "01",
			title: "Audit & cadrage",
			desc: "Analyse du site existant, identification des frictions dans le parcours de réservation, benchmark des restaurants concurrents et définition des objectifs de conversion.",
			tag: "Semaine 1",
		},
		{
			step: "02",
			title: "Direction artistique",
			desc: "Création de la charte graphique digitale, sélection typographique, palette de couleurs et direction photo pour les visuels culinaires.",
			tag: "Semaine 2",
		},
		{
			step: "03",
			title: "Développement",
			desc: "Intégration React, développement du système de réservation, menu digital dynamique, optimisation des performances et responsive mobile.",
			tag: "Semaines 3-5",
		},
		{
			step: "04",
			title: "SEO & lancement",
			desc: "Balisage schema.org, optimisation des métadonnées, création des pages locales et déploiement avec suivi des performances dès J+1.",
			tag: "Semaine 6",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Brasserie Carougeoise — Site web restaurant gastronomique",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						about: {
							"@type": "Restaurant",
							name: "Brasserie Carougeoise",
							address: {
								"@type": "PostalAddress",
								addressLocality: "Carouge",
								addressCountry: "CH",
							},
							servesCuisine: "Française",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-stone-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 15% 60%, #fef3c7 0%, transparent 50%), radial-gradient(circle at 85% 25%, #f5f5f4 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
							🍽️ Restaurant gastronomique
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">
							🇨🇭 Carouge, Genève
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Brasserie Carougeoise — Le restaurant gastronomique qui{" "}
						<span className="text-amber-700">affiche complet en ligne</span>
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a conçu le site web de la Brasserie Carougeoise, une adresse
						gastronomique emblématique de Carouge. Un projet centré sur un
						objectif clair : transformer les visiteurs du site en convives —
						avec un système de réservation en ligne fluide, un design qui met
						l'eau à la bouche et une visibilité locale optimisée sur Google.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un restaurant réputé, invisible sur le web
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								La Brasserie Carougeoise est une institution dans le quartier de
								Carouge à Genève. Cuisine française de qualité, cadre
								chaleureux, service attentionné — une réputation solide
								construite année après année. Pourtant, la présence digitale du
								restaurant était largement en dessous de ce que son niveau
								méritait.
							</p>
							<p>
								Sans réservation en ligne, les clients devaient appeler — et
								raccrochaient souvent sans réserver. Sans site mobile optimisé,
								les chercheurs de restaurants sur smartphone rebondissaient vers
								des concurrents mieux positionnés. Sans SEO local, la Brasserie
								n'apparaissait pas sur les requêtes{" "}
								<em>"restaurant Carouge"</em> ou <em>"brasserie Genève"</em>.
							</p>
							<p>
								La mission de Weblax : créer un site à la hauteur de la
								réputation du restaurant, avec un système de réservation qui
								travaille pour l'établissement 24h/24.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Situation avant refonte
							</div>
							{[
								{
									label: "Réservations en ligne",
									value: "Impossible",
									color: "text-red-500",
								},
								{
									label: "Position Google 'restaurant Carouge'",
									value: "Page 3+",
									color: "text-red-500",
								},
								{
									label: "Score mobile (PageSpeed)",
									value: "41/100",
									color: "text-orange-500",
								},
								{
									label: "Temps passé sur le site",
									value: "<45 secondes",
									color: "text-orange-500",
								},
								{
									label: "Menu digital",
									value: "PDF non mobile",
									color: "text-red-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Brief ── */}
				<section className="bg-gradient-to-r from-amber-50 to-stone-50 border border-amber-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Objectifs
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que la Brasserie Carougeoise attendait
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "📅",
								text: "Un système de réservation en ligne simple et efficace, sans friction",
							},
							{
								icon: "🍽️",
								text: "Un menu digital élégant, facile à mettre à jour et agréable à lire",
							},
							{
								icon: "📍",
								text: "Apparaître en tête des résultats Google sur Carouge et Genève",
							},
							{
								icon: "📱",
								text: "Une expérience mobile irréprochable pour les clients en déplacement",
							},
							{
								icon: "🎨",
								text: "Un design qui reflète le standing gastronomique de l'établissement",
							},
							{
								icon: "⭐",
								text: "Mettre en avant les avis positifs et renforcer la confiance en ligne",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-amber-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Fonctionnalités ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Solution
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Un site qui travaille pour le restaurant, pas l'inverse
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Pour un restaurant, le site web n'est pas un accessoire de
						communication — c'est un outil commercial actif. Chaque page, chaque
						bouton, chaque photo doit concourir à un seul objectif : transformer
						un visiteur curieux en client assis à table.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-amber-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Timeline ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Déroulement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						6 semaines pour transformer la présence digitale
					</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{timeline.map((item) => (
							<div
								key={item.step}
								className="p-6 rounded-2xl border border-border bg-white"
							>
								<div className="flex items-center justify-between mb-3">
									<span className="text-2xl font-bold text-amber-200">
										{item.step}
									</span>
									<span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
										{item.tag}
									</span>
								</div>
								<h3 className="font-semibold text-foreground mb-2">
									{item.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">Technologies utilisées</h2>
					<div className="flex flex-wrap gap-3">
						{[
							{ name: "React", desc: "Interface dynamique" },
							{ name: "Next.js", desc: "SSR & SEO" },
							{ name: "Tailwind CSS", desc: "Design system" },
							{ name: "Schema.org", desc: "Restaurant & LocalBusiness" },
							{ name: "Stripe", desc: "Acomptes réservation" },
							{ name: "Nodemailer", desc: "Confirmations auto" },
							{ name: "Vercel", desc: "Déploiement" },
						].map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Des tables qui se remplissent — en ligne
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Un mois après le lancement, les résultats dépassent les attentes
						initiales. Le restaurant reçoit désormais la majorité de ses
						réservations via le site, libérant du temps en salle et réduisant
						les no-shows grâce aux confirmations automatiques.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+120%",
								detail: "de réservations en ligne",
								desc: "Le système de réservation intégré génère en un mois plus de réservations en ligne que l'établissement n'en recevait en six mois par téléphone.",
								color: "text-amber-700",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
							{
								metric: "-50%",
								detail: "d'appels téléphoniques pour réserver",
								desc: "Les équipes en salle passent moins de temps à répondre au téléphone et plus de temps à s'occuper des clients présents.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "Top 3",
								detail: "sur les requêtes SEO ciblées",
								desc: "La Brasserie Carougeoise apparaît désormais dans les trois premiers résultats Google sur 'restaurant Carouge' et 'brasserie Genève centre'.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
							{
								metric: "+85%",
								detail: "de temps passé sur le site",
								desc: "Les menus, les photos et le storytelling du restaurant retiennent les visiteurs — et les convertissent en clients.",
								color: "text-rose-600",
								bg: "from-rose-50 to-pink-50",
								border: "border-rose-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-amber-100 bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🍽️</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"On avait du mal à croire qu'un site web pouvait autant changer les
						choses. Depuis le lancement, on affiche complet presque tous les
						week-ends — et on reçoit des réservations même quand on dort."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Direction Brasserie Carougeoise — Carouge, Genève
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Pour un restaurant, le site web est le meilleur serveur
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							La Brasserie Carougeoise démontre qu'un site web bien conçu est
							l'un des investissements les plus rentables qu'un restaurant
							puisse faire. Il travaille 24h/24, prend des réservations pendant
							le service, répond aux questions des clients et améliore la
							visibilité locale — sans jamais demander de pause.
						</p>
						<p>
							Pour les restaurateurs, l'enjeu n'est plus d'avoir "un site" —
							mais d'avoir un site qui convertit : rapide sur mobile, bien
							référencé localement, et doté d'un système de réservation qui
							simplifie la vie de tout le monde.
						</p>
						<p>
							C'est exactement ce que Weblax a livré pour la Brasserie
							Carougeoise — et c'est ce que nous pouvons faire pour votre
							établissement.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre restaurant mérite un site qui affiche complet
							</h3>
							<p className="text-sm opacity-70">
								Réservation en ligne, SEO local, design gastronomique — parlons
								de votre projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-amber-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
